/**
 * 通用工具函数
 */

/**
 * 防抖函数
 * @param {Function} fn - 要防抖的函数
 * @param {number} delay - 延迟时间（毫秒）
 * @returns {Function}
 */
export function debounce(fn, delay = 300) {
  let timeoutId
  return function (...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn.apply(this, args), delay)
  }
}

/**
 * 节流函数
 * @param {Function} fn - 要节流的函数
 * @param {number} limit - 时间限制（毫秒）
 * @returns {Function}
 */
export function throttle(fn, limit = 300) {
  let inThrottle
  return function (...args) {
    if (!inThrottle) {
      fn.apply(this, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

/**
 * 缓存函数结果
 * @param {Function} fn - 要缓存的函数
 * @param {Function} keyFn - 生成缓存键的函数
 * @returns {Function}
 * @deprecated Use createCachedFunction from cache.js instead
 */
export function memoize(fn, keyFn = (...args) => JSON.stringify(args)) {
  // Import from cache.js for consistency
  const { createCachedFunction } = require('./cache.js')
  return createCachedFunction(fn, {
    keyGenerator: keyFn,
    maxSize: 100
  })
}

/**
 * 智能格式化数字，处理浮点数精度问题
 * @param {number} num - 数字
 * @param {number} decimals - 小数位数（可选，如果不提供则自动判断）
 * @param {number} step - 步长（可选，用于自动判断小数位数）
 * @param {boolean} useScientific - 是否对很小的数字使用科学计数法
 * @returns {string}
 */
export function formatNumber(num, decimals = null, step = null, useScientific = true) {
  if (!Number.isFinite(num)) {
    return String(num)
  }
  
  // 先进行精度修复，避免 1.6000000000002 这样的问题
  let rounded = num
  
  // 根据 step 自动判断小数位数
  if (decimals === null && step !== null) {
    const stepStr = String(step)
    if (stepStr.includes('e') || stepStr.includes('E')) {
      // 科学计数法
      const match = stepStr.match(/e([+-]?\d+)/i)
      if (match) {
        const exp = parseInt(match[1])
        decimals = Math.max(0, -exp + 2)
      } else {
        decimals = 2
      }
    } else if (stepStr.includes('.')) {
      // 小数
      const parts = stepStr.split('.')
      decimals = parts[1] ? parts[1].length : 0
    } else {
      // 整数
      decimals = 0
    }
  }
  
  // 如果仍然没有指定小数位数，使用默认值
  if (decimals === null) {
    decimals = 2
  }
  
  // 先 round 到指定精度，避免浮点数误差
  const factor = Math.pow(10, decimals)
  rounded = Math.round(num * factor) / factor
  
  // 对于很小的数字，使用科学计数法
  if (useScientific && Math.abs(rounded) > 0 && Math.abs(rounded) < 0.001) {
    return rounded.toExponential(2)
  }
  
  // 对于很大的数字，也考虑使用科学计数法
  if (useScientific && Math.abs(rounded) >= 1e6) {
    return rounded.toExponential(2)
  }
  
  // 使用 toFixed，但先确保精度
  const fixed = rounded.toFixed(decimals)
  
  // 移除末尾不必要的零（但保留至少一位小数，如果原数有小数部分）
  if (decimals > 0) {
    const numStr = fixed.replace(/\.?0+$/, '')
    // 如果移除后没有小数点了，但原数确实有小数部分，保留一位
    if (!numStr.includes('.') && num % 1 !== 0) {
      return rounded.toFixed(1).replace(/\.?0+$/, '')
    }
    return numStr
  }
  
  return fixed
}

/**
 * 格式化数字（旧版本兼容）
 * @param {number} num - 数字
 * @param {number} decimals - 小数位数
 * @returns {string}
 */
export function formatNumberLegacy(num, decimals = 2) {
  return formatNumber(num, decimals, null, false)
}

/**
 * 复制文本到剪贴板
 * @param {string} text - 要复制的文本
 * @returns {Promise<boolean>}
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (error) {
    console.error('Failed to copy to clipboard:', error)
    return false
  }
}

/**
 * 下载文件
 * @param {string} content - 文件内容
 * @param {string} filename - 文件名
 * @param {string} mimeType - MIME 类型
 */
export function downloadFile(content, filename, mimeType = 'text/plain') {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ============================================
// 数学工具函数
// ============================================

/**
 * 计算数组的平均值
 * @param {Array<number>} arr - 数字数组
 * @returns {number}
 */
export function mean(arr) {
  if (!arr || arr.length === 0) return 0
  return arr.reduce((sum, val) => sum + val, 0) / arr.length
}

/**
 * 计算数组的标准差
 * @param {Array<number>} arr - 数字数组
 * @returns {number}
 */
export function standardDeviation(arr) {
  if (!arr || arr.length === 0) return 0
  const avg = mean(arr)
  const squareDiffs = arr.map(val => Math.pow(val - avg, 2))
  return Math.sqrt(mean(squareDiffs))
}

/**
 * 计算数组的最小值
 * @param {Array<number>} arr - 数字数组
 * @returns {number}
 */
export function min(arr) {
  if (!arr || arr.length === 0) return 0
  return Math.min(...arr)
}

/**
 * 计算数组的最大值
 * @param {Array<number>} arr - 数字数组
 * @returns {number}
 */
export function max(arr) {
  if (!arr || arr.length === 0) return 0
  return Math.max(...arr)
}

/**
 * 线性插值
 * @param {number} x - 插值点
 * @param {number} x0 - 已知点 1 的 x 坐标
 * @param {number} y0 - 已知点 1 的 y 坐标
 * @param {number} x1 - 已知点 2 的 x 坐标
 * @param {number} y1 - 已知点 2 的 y 坐标
 * @returns {number}
 */
export function linearInterpolate(x, x0, y0, x1, y1) {
  if (x1 === x0) return y0
  return y0 + (x - x0) * (y1 - y0) / (x1 - x0)
}

/**
 * 将值限制在指定范围内
 * @param {number} value - 值
 * @param {number} min - 最小值
 * @param {number} max - 最大值
 * @returns {number}
 */
export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

/**
 * 生成指定范围内的等间距数组
 * @param {number} start - 起始值
 * @param {number} end - 结束值
 * @param {number} steps - 步数
 * @returns {Array<number>}
 */
export function linspace(start, end, steps) {
  if (steps <= 1) return [start]
  const arr = []
  const step = (end - start) / (steps - 1)
  for (let i = 0; i < steps; i++) {
    arr.push(start + step * i)
  }
  return arr
}

// ============================================
// 可视化辅助函数
// ============================================

/**
 * 将数据坐标转换为画布坐标
 * @param {number} dataX - 数据 X 坐标
 * @param {number} dataY - 数据 Y 坐标
 * @param {Object} dataRange - 数据范围 {xMin, xMax, yMin, yMax}
 * @param {Object} canvasSize - 画布大小 {width, height}
 * @param {Object} padding - 内边距 {top, right, bottom, left}
 * @returns {Object} {x, y}
 */
export function dataToCanvas(dataX, dataY, dataRange, canvasSize, padding = {}) {
  const { xMin, xMax, yMin, yMax } = dataRange
  const { width, height } = canvasSize
  const { top = 0, right = 0, bottom = 0, left = 0 } = padding
  
  const plotWidth = width - left - right
  const plotHeight = height - top - bottom
  
  const x = left + ((dataX - xMin) / (xMax - xMin)) * plotWidth
  const y = height - bottom - ((dataY - yMin) / (yMax - yMin)) * plotHeight
  
  return { x, y }
}

/**
 * 将画布坐标转换为数据坐标
 * @param {number} canvasX - 画布 X 坐标
 * @param {number} canvasY - 画布 Y 坐标
 * @param {Object} dataRange - 数据范围 {xMin, xMax, yMin, yMax}
 * @param {Object} canvasSize - 画布大小 {width, height}
 * @param {Object} padding - 内边距 {top, right, bottom, left}
 * @returns {Object} {x, y}
 */
export function canvasToData(canvasX, canvasY, dataRange, canvasSize, padding = {}) {
  const { xMin, xMax, yMin, yMax } = dataRange
  const { width, height } = canvasSize
  const { top = 0, right = 0, bottom = 0, left = 0 } = padding
  
  const plotWidth = width - left - right
  const plotHeight = height - top - bottom
  
  const x = xMin + ((canvasX - left) / plotWidth) * (xMax - xMin)
  const y = yMin + ((height - bottom - canvasY) / plotHeight) * (yMax - yMin)
  
  return { x, y }
}

/**
 * 计算合适的坐标轴刻度（基础版本，使用 Nice Number 算法）
 * 
 * @param {number} min - 最小值
 * @param {number} max - 最大值
 * @param {number} maxTicks - 最大刻度数
 * @returns {Array<number>} 刻度值数组
 */
export function calculateTicks(min, max, maxTicks = 10) {
  const range = max - min
  const roughStep = range / (maxTicks - 1)
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)))
  const normalizedStep = roughStep / magnitude
  
  let step
  if (normalizedStep <= 1) step = magnitude
  else if (normalizedStep <= 2) step = 2 * magnitude
  else if (normalizedStep <= 5) step = 5 * magnitude
  else step = 10 * magnitude
  
  const tickMin = Math.floor(min / step) * step
  const tickMax = Math.ceil(max / step) * step
  
  const ticks = []
  for (let tick = tickMin; tick <= tickMax; tick += step) {
    ticks.push(tick)
  }
  
  return ticks
}

/**
 * 判断是否应该使用 Canvas 而非 SVG
 * @param {number} dataPoints - 数据点数量
 * @param {number} threshold - 阈值
 * @returns {boolean}
 */
export function shouldUseCanvas(dataPoints, threshold = 1000) {
  return dataPoints > threshold
}
